import { REPORTING_LE_DATA_ASK_PATH } from '~/consts/reportingLeDataApi';

import type {
  LeDataContentBlock,
  LeDataContentType,
  LeDataEngine,
  LeDataImageAttachment,
  LeDataStreamHandlers,
  LeDataUsageSummary,
  LensIntent,
  MantleMode,
} from '~/types/leData';

import { encodeRequestBody } from '~/utils/encodeRequestBody';
import { formatLeDataToolLabel } from '~/utils/leData/formatToolLabel';
import { getLeDataApiErrorMessage } from '~/utils/leData/getLeDataApiErrorMessage';
import { parseLeDataProvenance } from '~/utils/leData/provenance';
import { resolveReportingLeDataUrl } from '~/utils/resolveReportingLeDataUrl';
import { SseHttpError, fetchSsePost } from '~/utils/sse/fetchSsePost';

function reportingLeDataAskUrl(): string {
  return resolveReportingLeDataUrl(REPORTING_LE_DATA_ASK_PATH);
}

const CONTENT_TYPES = new Set<LeDataContentType>([
  'text',
  'sql',
  'table',
  'chart',
  'file',
  'error',
  'lens_draft',
  'lens_scope',
  'lens_plan',
  'lens_created',
]);

function newBlockId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `block-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function safeJsonParse(raw: string): Record<string, unknown> | null {
  if (!raw?.trim()) {
    return null;
  }
  try {
    const v = JSON.parse(raw) as unknown;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      return v as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * True when a content-bearing frame did not arrive as the JSON object the stream contract
 * requires. `SSEEmitter.send` always writes `JSON.stringify(data)` for an object payload
 * (srp-lens-contract src/modules/le-data/sse-emitter.ts), so an unparseable content frame means the
 * bytes were truncated or corrupted between the server and here. Such a frame carries no answer:
 * rendering its raw text puts a JSON fragment on screen where a number belongs, so it is discarded
 * and the turn is marked damaged instead.
 *
 * Ephemeral frames (`progress`, `ping`) are excluded: they are narration, never an answer, and the
 * progress parser already has a plain-string path for older servers.
 */
export function isMalformedContentFrame(eventName: string, data: string): boolean {
  const ev = eventName.trim().toLowerCase();
  const isContentFrame = ev === 'thinking' || ev === 'content' || CONTENT_TYPES.has(ev as LeDataContentType);
  if (!isContentFrame) {
    return false;
  }
  if (!data.trim()) {
    // An empty content frame adds nothing but is not evidence of damage.
    return false;
  }
  return safeJsonParse(data) === null;
}

function parseProgressMessage(data: string): string | null {
  const trimmed = data.trim();
  if (!trimmed || trimmed === '{}') {
    return null;
  }
  const obj = safeJsonParse(trimmed);
  if (obj && typeof obj.tool === 'string' && obj.tool.trim()) {
    const label = formatLeDataToolLabel(obj.tool.trim());
    const phase = typeof obj.status === 'string' ? obj.status.toLowerCase() : '';
    if (phase === 'calling') {
      return `${label}…`;
    }
    if (phase === 'complete') {
      const lat = typeof obj.latencyMs === 'number' && Number.isFinite(obj.latencyMs) ? ` (${obj.latencyMs} ms)` : '';
      return `${label} · done${lat}`;
    }
    return label;
  }
  if (obj) {
    const msg =
      (typeof obj.message === 'string' && obj.message) ||
      (typeof obj.text === 'string' && obj.text) ||
      (typeof obj.progress === 'string' && obj.progress);
    if (msg) {
      return msg;
    }
    if (typeof obj.status === 'string' && obj.status && typeof obj.tool !== 'string') {
      return obj.status;
    }
  }
  return trimmed;
}

function normalizeContentType(eventName: string, payload: Record<string, unknown>): LeDataContentType {
  const fromPayload = typeof payload.type === 'string' ? payload.type.toLowerCase() : '';
  if (fromPayload === 'thinking') {
    return 'text';
  }
  if (fromPayload && CONTENT_TYPES.has(fromPayload as LeDataContentType)) {
    return fromPayload as LeDataContentType;
  }
  const ev = eventName.toLowerCase();
  if (ev === 'thinking') {
    return 'text';
  }
  if (CONTENT_TYPES.has(ev as LeDataContentType)) {
    return ev as LeDataContentType;
  }
  return 'text';
}

/** Best-effort plain text of a thinking/delta payload: first non-empty of the usual text keys, else the raw data. */
function pickEventText(data: string, parsed: Record<string, unknown> | null): string {
  return (
    (typeof parsed?.markdown === 'string' && parsed.markdown.trim()) ||
    (typeof parsed?.text === 'string' && parsed.text.trim()) ||
    (typeof parsed?.content === 'string' && parsed.content.trim()) ||
    (typeof parsed?.message === 'string' && parsed.message.trim()) ||
    data.trim()
  );
}

function buildThinkingPayload(data: string, parsed: Record<string, unknown> | null): Record<string, unknown> | null {
  const text = pickEventText(data, parsed);

  if (!text) {
    return null;
  }

  return {
    ...(parsed ?? {}),
    markdown: text,
    section: 'thought-process',
  };
}

function buildContentBlock(eventName: string, data: string): LeDataContentBlock {
  const parsed = safeJsonParse(data);
  const payload =
    eventName.toLowerCase() === 'thinking' || parsed?.type === 'thinking'
      ? (buildThinkingPayload(data, parsed) ?? { markdown: data, section: 'thought-process' })
      : (parsed ?? { body: data });
  const type = normalizeContentType(eventName, payload);
  return {
    id: newBlockId(),
    type,
    payload,
  };
}

/**
 * Dispatch one live SSE frame. Reshow projects the stored ledger instead — ephemera never persist.
 */
export function dispatchSseEvent(eventName: string, data: string, handlers: LeDataStreamHandlers): void {
  const ev = eventName.trim().toLowerCase();

  if (ev === 'done') {
    const obj = safeJsonParse(data);
    const sessionId = typeof obj?.sessionId === 'string' ? obj.sessionId : undefined;
    const usage = obj?.usage && typeof obj.usage === 'object' ? (obj.usage as LeDataUsageSummary) : undefined;
    handlers.onDone({ sessionId, usage });
    return;
  }

  if (ev === 'session') {
    const obj = safeJsonParse(data);
    const sessionId = typeof obj?.sessionId === 'string' ? obj.sessionId : undefined;
    if (sessionId) {
      handlers.onSession?.(sessionId);
    }
    return;
  }

  if (ev === 'ping') {
    return;
  }

  if (ev === 'progress') {
    handlers.onProgress(parseProgressMessage(data));
    return;
  }

  if (ev === 'provenance') {
    const provenance = parseLeDataProvenance(safeJsonParse(data));
    if (provenance) {
      handlers.onProvenance?.(provenance);
    }
    return;
  }

  if (ev === 'steered') {
    const obj = safeJsonParse(data);
    const text = typeof obj?.text === 'string' ? obj.text.trim() : '';
    if (text) {
      handlers.onSteered?.(text);
    }
    return;
  }

  if (ev === 'delta') {
    // Token chunks are passed through VERBATIM (no trim): whitespace between tokens is content.
    // A consumer-less delta is simply dropped - the v1 renderer's graceful degradation.
    const obj = safeJsonParse(data);
    const chunk = typeof obj?.content === 'string' ? obj.content : '';
    if (chunk) {
      handlers.onDelta?.(chunk);
    }
    return;
  }

  if (ev === 'thinking' || ev === 'content' || CONTENT_TYPES.has(ev as LeDataContentType)) {
    if (isMalformedContentFrame(ev, data)) {
      // Dropped on purpose. `streamReportingAiAsk` sees the same frame and marks the turn damaged,
      // which is what tells the reader the answer is not whole.
      return;
    }
    try {
      if (ev === 'thinking') {
        // Side signal for the stream live line; the content block below is the persistent record.
        const text = pickEventText(data, safeJsonParse(data));
        if (text) {
          handlers.onThinking?.(text);
        }
      }
      handlers.onContent(buildContentBlock(ev, data));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to parse content event';
      handlers.onStreamError?.(msg);
    }
  }
}

export type LeDataAskOrigin = 'chat' | 'lens_build' | 'lens_chat' | 'lens_create';

export interface StreamReportingAiAskParams {
  question: string;
  /** Omit or empty string to start a new session */
  sessionId?: string | null;
  /**
   * Whether a person deliberately started this conversation. Background Lens
   * planning and card-generation runs are persisted for auditability, but are
   * not personal chats and must not appear in chat history.
   */
  humanInitiated?: boolean;
  /** A background insight answers a question without offering or applying Lens edits. */
  purpose?: 'lens_insight';
  /** Optional image attachments (base64, max 5, each under 5MB) */
  images?: Array<LeDataImageAttachment>;
  /**
   * New-session engine hint. The server births mantle unless `invoke` is asked
   * for by name. Sticky once born, so it is only sent for a new session.
   */
  engine?: LeDataEngine;
  /** New-session mantle mode (`standard`/`extended`); birth-pinned like engine. */
  mode?: MantleMode;
  /**
   * Reasoning depth for this turn alone, unlike engine and mode. Sent by
   * callers whose turn is mechanical, where depth buys nothing and the tokens
   * it spends are wall-clock.
   */
  effort?: 'low' | 'medium' | 'high';
  /** What opened the session; birth-pinned like engine. Machine origins are kept out of the session list. */
  origin?: LeDataAskOrigin;
  /** Lens the session belongs to, so build turns and the Lens's own reads share one id. */
  lensId?: string;
  /** Concrete Lens period values for executing SQL that keeps @start_date/@end_date in its saved form. */
  lensPeriod?: { startDate: string; endDate: string };
  /**
   * Present only for a turn the Lens-create conversation drives deterministically (a "New Lens"
   * door, a scope-chip click, or the `?view=lens&plan=` door). When set, the server skips the
   * free-thread agent and runs `runLensIntent` instead, emitting `lens_scope`/`lens_plan` blocks.
   * `question` still carries the reader-facing turn text (the brief or the chip label) so the
   * turn reads the same in history either way.
   */
  lensIntent?: LensIntent;
  /** The complete context for this turn. The server forwards only what changed and never shows it to the reader. */
  context?: ReadonlyArray<LeDataContextBlock>;
}

export interface LeDataContextBlock {
  kind: string;
  text: string;
  applies?: 'this_turn';
}

/**
 * POST le-data/ask as SSE. `done` may carry sessionId for follow-ups; progress cleared in finally.
 */
export async function streamReportingAiAsk(
  params: StreamReportingAiAskParams,
  handlers: LeDataStreamHandlers,
  options?: { signal?: AbortSignal },
): Promise<void> {
  const body: Record<string, unknown> = {
    question: params.question,
    sessionId: params.sessionId ?? '',
  };
  if (params.images && params.images.length > 0) {
    body.images = params.images;
  }
  if (params.engine === 'mantle' && !params.sessionId) {
    body.engine = params.engine;
  }
  if (params.mode && !params.sessionId) {
    body.mode = params.mode;
  }
  if (params.effort) {
    body.effort = params.effort;
  }
  if (params.lensPeriod) {
    body.lensPeriod = params.lensPeriod;
  }
  // Birth metadata is immutable. A follow-up never gets to reclassify a
  // session someone already created. Do not simplify to `!params.humanInitiated`: undefined (the
  // ordinary case; no caller passes this for a real chat) must not send `humanInitiated: false`,
  // and this rule's own autofix has rewritten this exact comparison into that broken form before.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
  if (!params.sessionId && params.humanInitiated === false) {
    body.humanInitiated = false;
  }
  if (params.origin && !params.sessionId) {
    body.origin = params.origin;
  }
  if (params.lensId) {
    body.lensId = params.lensId;
  }
  if (params.purpose) {
    body.purpose = params.purpose;
  }
  if (params.lensIntent) {
    body.lensIntent = params.lensIntent;
  }
  if (params.context?.length) {
    body.context = params.context;
  }

  // Distinguish a clean finish (terminal `done` event seen) from a truncated one, and a
  // deliberate cancellation (AbortSignal) from an unexpected drop, so the caller can decide
  // whether to warn the user that the answer was cut off.
  let sawTerminalEvent = false;
  let aborted = false;
  // A frame that could not be parsed was discarded rather than rendered, so the answer on screen
  // is missing part of itself even if a terminal `done` still arrives.
  let sawMalformedFrame = false;
  // The server's own `error` event already explains itself; the caller uses this to avoid stacking
  // a second, vaguer "cut off" message on top of it.
  let sawErrorEvent = false;

  try {
    await fetchSsePost(
      reportingLeDataAskUrl(),
      encodeRequestBody(body),
      (eventName, data) => {
        const ev = eventName.trim().toLowerCase();
        if (ev === 'done') {
          sawTerminalEvent = true;
        }
        if (ev === 'error') {
          sawErrorEvent = true;
        }
        if (isMalformedContentFrame(ev, data)) {
          sawMalformedFrame = true;
        }
        dispatchSseEvent(eventName, data, handlers);
      },
      {
        signal: options?.signal,
      },
    );
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      aborted = true;
      return;
    }
    if (e instanceof SseHttpError && e.code) {
      // A coded refusal is already worded for the user, so its prose is preferred. It still goes
      // through the mapper, because a 500 body can carry a code alongside a stack trace.
      handlers.onStreamError?.(getLeDataApiErrorMessage(e.detail || e.message, { status: e.status }), {
        status: e.status,
        code: e.code,
      });
    } else if (e instanceof SseHttpError) {
      handlers.onStreamError?.(getLeDataApiErrorMessage(e, { status: e.status }), { status: e.status });
    } else {
      handlers.onStreamError?.(getLeDataApiErrorMessage(e));
    }
  } finally {
    handlers.onDone({
      completed: sawTerminalEvent,
      aborted,
      damaged: sawMalformedFrame,
      failed: sawErrorEvent,
    });
  }
}
